import {
  isOffHireEntry,
  isSingleEntry,
  EventCategory,
} from '@seaber/common/core/entries';
import { isCargoEntry, isScheduleCargoEvent } from './core/TimeData';
import type {
  CoaEntry,
  PortCallEntry,
  SpotEntry,
} from '@seaber/common/core/entries';
import { CoreEventType } from './core/TimeData';
import { dateStringToNumOpt, numToDateString } from './core/util';
import {
  assertedDefined,
  filteredMap,
  isDefined,
  mapDropNulls,
  mapKeyBy,
  memoize3,
  NONE,
} from '../lib/util';
import { findLastIndex, orderBy, partition } from 'lodash';
import type { ClientId } from './core/ClientId';
import type {
  TCScheduleEntry,
  ScheduleEntry,
  ScenarioOrderPatch,
} from './Scenario';
import {
  isMasterScenario,
  getScenarioUpdatedAt,
  EntryState,
  isUserScenario,
  isSuggestionScenario,
  isBackupScenario,
  isInitialScenario,
  buildDeletedAssignmentPatch,
} from './Scenario';
import type {
  UserSchedule,
  ScenarioSchedule,
  MasterSchedule,
  SuggestionSchedule,
  CoreScenario,
  BackupSchedule,
  InitialSchedule,
} from '@seaber/common/core/scenario';
import { mapGroupBy } from '@seaber/common/lib/collections';

export type MergeDirectives = {
  /** Forces one shipment after another. Key is the shipment to move, value is the shipment to place after. */
  placeShipmentAfter?: Map<ClientId, ClientId>;
};

export type MergeScenarioOptions = {
  sourceIsCommit: boolean;
  directives?: MergeDirectives;
};

// Stable singletons so the memoize3 WeakMap key on `opts` actually hits
// when the only variation is sourceIsCommit and no directives are supplied.
export const MERGE_SOURCE_IS_COMMIT: MergeScenarioOptions = {
  sourceIsCommit: true,
};
export const MERGE_SOURCE_NOT_COMMIT: MergeScenarioOptions = {
  sourceIsCommit: false,
};

/**
 * Resolve the `id` for a merged scenario. Prefers the target's id, but falls
 * back to the source's id when the target has none — e.g. when a stale local
 * copy (id=null) is merged with a server-pushed update that has a real id.
 */
const resolveMergedScenarioId = (
  target: UserSchedule | SuggestionSchedule,
  source: ScenarioSchedule,
): number | null =>
  target.id ??
  (isUserScenario(source) || isSuggestionScenario(source) ? source.id : null);

/**
 * Combine two scenarios, or a scenario with committed state.
 * As a general rule the state in source overwrites state in target if there are overlapping changes.
 * If one of the scenarios is the committed state and not a full scenario, it must be the second parameter.
 */
export const getMergedScenario = memoize3(function getMergedScenario<
  T extends ScenarioSchedule,
  U extends ScenarioSchedule,
>(originalTarget: T, originalSource: U, opts: MergeScenarioOptions): T {
  if (
    'type' in originalTarget &&
    isMasterScenario(originalTarget) &&
    opts.sourceIsCommit
  ) {
    // Updating committed (master) from latest committed data, make a quick replace
    // noinspection UnnecessaryLocalVariableJS
    const result: T = {
      ...originalTarget,
      schedule: originalSource.schedule,
      coaAssignments: originalSource.coaAssignments,
      spotAssignments: originalSource.spotAssignments,
      portCalls: originalSource.portCalls,
      cargoAllocations: originalSource.cargoAllocations,
      freightByShipmentClientId: originalSource.freightByShipmentClientId,
      extraFreightByShipmentClientId:
        originalSource.extraFreightByShipmentClientId,
      updatedAt: new Date().toISOString(),
    };
    return result;
  }

  const [target, source] = opts.sourceIsCommit
    ? [originalTarget, originalSource]
    : orderBy<ScenarioSchedule>([originalTarget, originalSource], (s) =>
        getScenarioUpdatedAt(s),
      );

  const targetAllEntries = new Map(
    [
      ...target.schedule,
      ...target.coaAssignments,
      ...target.spotAssignments,
    ].map((e) => [e.clientId, e]),
  );

  const sourceAllEntries = new Map(
    [
      ...source.schedule,
      ...source.coaAssignments,
      ...source.spotAssignments,
    ].map((e) => [e.clientId, e]),
  );

  const getEntryStates = (s: ScenarioSchedule): UserSchedule['entryStates'] => {
    if ('entryStates' in s) {
      return s.entryStates;
    } else {
      return new Map();
    }
  };

  const targetEntryStates = getEntryStates(target);
  const sourceEntryStates = getEntryStates(source);

  const isControlled = (
    states: UserSchedule['entryStates'],
    entry: { clientId: ClientId },
  ) => states.get(entry.clientId) === EntryState.UPDATED;

  const isControlledInScenario = (
    scenario: ScenarioSchedule | null,
    entry: { clientId: ClientId },
  ): boolean =>
    !!scenario &&
    'entryStates' in scenario &&
    isControlled(scenario.entryStates, entry);

  /** These entries are no longer present in the new commit, and they were not touched in the target scenario.
   * => These are assumed to be deleted entries unrelated to current scenario in any way and can be dropped.
   * If the source is not the state from the db the scenario might be missing an entry just because it's not up-to-date, so we cannot delete it in this case.
   */
  const disappearedEntries = !opts.sourceIsCommit
    ? new Map()
    : new Map(
        [...targetAllEntries.values()]
          .filter(
            (e) =>
              !sourceAllEntries.has(e.clientId) &&
              !isControlled(targetEntryStates, e),
          )
          .map((e) => [e.clientId, e]),
      );

  /** Entry (by client id) considered deleted when it's deleted in the newer scenario, or deleted in older while not updated in newer */
  const isDeleted = (entry: { clientId: ClientId }) =>
    sourceEntryStates.get(entry.clientId) === EntryState.DELETED ||
    (targetEntryStates.get(entry.clientId) === EntryState.DELETED &&
      !isControlled(sourceEntryStates, entry));

  const isScheduleItem = (
    e: TCScheduleEntry | CoaEntry | SpotEntry,
  ): e is TCScheduleEntry =>
    e.category === EventCategory.TC ||
    e.category === EventCategory.Single ||
    e.category === EventCategory.OffHire;
  const isCoaItem = (
    e: TCScheduleEntry | CoaEntry | SpotEntry,
  ): e is CoaEntry => e.category === EventCategory.COA;
  const isSpotItem = (
    e: TCScheduleEntry | CoaEntry | SpotEntry,
  ): e is SpotEntry => e.category === EventCategory.Spot;

  const targetOriginalAssignments = new Map(
    [...targetAllEntries.values()]
      .filter(isScheduleCargoEvent)
      .map((e) => [e.orderId, e]),
  );

  const sourceOriginalAssignments = new Map(
    [...sourceAllEntries.values()]
      .filter(isScheduleCargoEvent)
      .map((e) => [e.orderId, e]),
  );

  const allAssignedOrderIds = new Set([
    ...targetOriginalAssignments.keys(),
    ...sourceOriginalAssignments.keys(),
  ]);

  const resolveKeptEntry = <T extends ScheduleEntry | PortCallEntry>(
    targetEntry: T | null,
    sourceEntry: T | null,
    respectDeletion = true,
  ): T | null => {
    const presentTargetEntry =
      targetEntry &&
      (respectDeletion && isDeleted(targetEntry) ? null : targetEntry);
    const presentSourceEntry =
      sourceEntry &&
      (respectDeletion && isDeleted(sourceEntry) ? null : sourceEntry);

    // If one doesn't exist after deletions are considered, use the other (if that one exists)
    if (!presentTargetEntry) {
      return presentSourceEntry;
    } else if (!presentSourceEntry) {
      return presentTargetEntry;
    }

    const targetEntryIsControlled = isControlled(
      targetEntryStates,
      presentTargetEntry,
    );
    const sourceEntryIsControlled = isControlled(
      sourceEntryStates,
      presentSourceEntry,
    );

    /** Integration master scenario assignment always takes precedence. Note: source must be prioritized to allow scenario to still update from a newer version of itself. */
    if (source.isIntegrationMaster) {
      return presentSourceEntry;
    } else if (target.isIntegrationMaster) {
      return presentTargetEntry;
    }

    // If one is explicitly controlled (created/updated) in its scenario and the other one is not, use the controlled one
    if (targetEntryIsControlled && !sourceEntryIsControlled) {
      return presentTargetEntry;
    } else if (sourceEntryIsControlled && !targetEntryIsControlled) {
      return presentSourceEntry;
    }

    // If one entry is newer, use that one
    if (presentTargetEntry.updatedAt && presentSourceEntry.updatedAt) {
      if (presentTargetEntry.updatedAt > presentSourceEntry.updatedAt) {
        return presentTargetEntry;
      } else if (presentSourceEntry.updatedAt > presentTargetEntry.updatedAt) {
        return presentSourceEntry;
      }
    }

    // No other way to figure it out automatically, just use new source. This case isn't very likely to happen after all the others.
    return presentSourceEntry;
  };

  /** For order assignments present in both scenarios, resolved the one to keep */
  const resultOrderAssignments = new Map(
    [...allAssignedOrderIds].map((orderId) => {
      const target = targetOriginalAssignments.get(orderId) ?? null;
      const source = sourceOriginalAssignments.get(orderId) ?? null;
      return [orderId, resolveKeptEntry(target, source, true)];
    }),
  );

  /**
   * From source keep everything that isn't explicitly deleted.
   * Also keep deleted items if they are created or updated in source (often the case with alternate scenarios).
   * As an exception when source is the committed state we don't want to overwrite order assignments which are edited in target scenario.
   */
  const sourceAllKeptEntries = new Map(
    [...sourceAllEntries.values()]
      .filter((e) => {
        if (isScheduleCargoEvent(e)) {
          return !isDeleted(e) && resultOrderAssignments.get(e.orderId) === e;
        } else {
          return !isDeleted(e);
        }
      })
      .map((e) => [e.clientId, e]),
  );

  /**
   * From target keep stuff that isn't deleted, or already present/assigned by source
   */
  const targetAllKeptEntries = new Map(
    [...targetAllEntries.values()]
      .filter((e) => {
        if (isScheduleCargoEvent(e)) {
          return (
            !isDeleted(e) &&
            resultOrderAssignments.get(e.orderId) === e &&
            !disappearedEntries.has(e.clientId)
          );
        } else {
          return !isDeleted(e) && !disappearedEntries.has(e.clientId);
        }
      })
      .map((e) => [e.clientId, e]),
  );

  const allKeptEntries = new Map(
    [
      ...new Set([
        ...targetAllKeptEntries.keys(),
        ...sourceAllKeptEntries.keys(),
      ]),
    ].map(
      (
        clientId,
      ): [
        ClientId,
        ScenarioSchedule['schedule'][number] | CoaEntry | SpotEntry,
      ] => {
        const targetEntry = targetAllKeptEntries.get(clientId);
        const sourceEntry = sourceAllKeptEntries.get(clientId);
        if (targetEntry && sourceEntry) {
          if (targetEntry.updatedAt && sourceEntry.updatedAt) {
            return [
              clientId,
              isOffHireEntry(targetEntry) || isSingleEntry(targetEntry)
                ? // Order related entries are already resolved in this manner when collected to resultOrderAssignments, but it probably wouldn't even if we did it again here
                  assertedDefined(
                    resolveKeptEntry(targetEntry, sourceEntry, true),
                    `Iterating ids one of the items expected to be defined`,
                  )
                : targetEntry.updatedAt > sourceEntry.updatedAt
                  ? targetEntry
                  : sourceEntry,
            ];
          } else {
            return [clientId, sourceEntry];
          }
        }

        return [
          clientId,
          assertedDefined(
            sourceEntry ?? targetEntry,
            `Iterating ids one of the items expected to be defined`,
          ),
        ];
      },
    ),
  );

  const targetCargoAllocationOrderIds = new Set(
    target.cargoAllocations?.map((allocation) => allocation.orderId),
  );
  const sourceCargoAllocationOrderIds = new Set(
    source.cargoAllocations?.map((allocation) => allocation.orderId),
  );
  const targetCargoAllocations =
    target.cargoAllocations?.filter((allocation) => {
      const { orderId, scheduledOrderClientId } = allocation;

      const kept = allKeptEntries.has(scheduledOrderClientId);

      const scheduledInThisScenario =
        resultOrderAssignments.get(orderId) ===
        targetOriginalAssignments.get(orderId);

      const allocatedInOtherScenario =
        sourceCargoAllocationOrderIds.has(orderId);

      return kept && (scheduledInThisScenario || !allocatedInOtherScenario);
    }) ?? [];
  const sourceCargoAllocations =
    source.cargoAllocations?.filter((allocation) => {
      const { orderId, scheduledOrderClientId } = allocation;

      const kept = allKeptEntries.has(scheduledOrderClientId);

      const scheduledInThisScenario =
        resultOrderAssignments.get(orderId) ===
        sourceOriginalAssignments.get(orderId);

      const allocatedInOtherScenario =
        targetCargoAllocationOrderIds.has(orderId);

      return kept && (scheduledInThisScenario || !allocatedInOtherScenario);
    }) ?? [];
  const cargoAllocations = [
    ...targetCargoAllocations,
    ...sourceCargoAllocations,
  ];

  const coaAssignments = [...allKeptEntries.values()].filter(isCoaItem);
  const spotAssignments = [...allKeptEntries.values()].filter(isSpotItem);
  /** Merged schedule, but times not adjusted yet */
  const unadjustedSchedule = [...allKeptEntries.values()].filter(
    isScheduleItem,
  );

  const entryFromWhichScenario = (
    a: ScheduleEntry,
  ): ScenarioSchedule | null => {
    if (targetAllKeptEntries.get(a.clientId) === a) {
      return target;
    } else if (sourceAllKeptEntries.get(a.clientId) === a) {
      return source;
    } else {
      return null;
    }
  };

  const voyageCharterByOrderId = mapDropNulls(
    new Map(
      [
        ...new Set([
          ...target.voyageCharterByOrderId.keys(),
          ...source.voyageCharterByOrderId.keys(),
        ]),
      ].map((orderId) => {
        const assignment = resultOrderAssignments.get(orderId);
        if (!assignment) {
          // Assignment has been deleted, we still want to keep the patch so that we can remove the relation at commit
          return [orderId, null];
        }
        const entryScenario = entryFromWhichScenario(assignment);
        return [
          orderId,
          entryScenario?.voyageCharterByOrderId.get(orderId) ?? null,
        ];
      }),
    ),
  );

  const patchOrders = mapDropNulls(
    new Map(
      [
        ...new Set([
          ...('patchOrders' in target ? target.patchOrders.keys() : []),
          ...('patchOrders' in source ? source.patchOrders.keys() : []),
        ]),
      ].map((orderId) => {
        // Select which scenario to use the order patch from.
        // Note that this selection logic relies heavily on shipments, although some other fields also exist
        const assignment = resultOrderAssignments.get(orderId);

        if (!assignment) {
          // Assignment has been deleted, we still want to keep the patch so that we can remove the relations at commit
          return [orderId, buildDeletedAssignmentPatch(orderId)];
        }
        const entryScenario = entryFromWhichScenario(assignment);
        const otherScenario = entryScenario === target ? source : target;
        const isAssignmentControlled = isControlledInScenario(
          entryScenario,
          assignment,
        );
        const entryScenarioPatch =
          (entryScenario &&
            'patchOrders' in entryScenario &&
            entryScenario.patchOrders.get(orderId)) ||
          null;
        const otherScenarioPatch =
          (otherScenario &&
            'patchOrders' in otherScenario &&
            otherScenario.patchOrders.get(orderId)) ||
          null;

        const entryShipmentId = entryScenarioPatch?.patch?.shipmentId;
        const otherShipmentId = otherScenarioPatch?.patch?.shipmentId;

        const isEntryShipmentControlled =
          typeof entryShipmentId === 'string' &&
          isControlledInScenario(entryScenario, {
            clientId: entryShipmentId,
          });
        const isOtherShipmentControlled =
          typeof otherShipmentId === 'string' &&
          isControlledInScenario(otherScenario, {
            clientId: otherShipmentId,
          });

        /** When the selected assignment itself is controlled in scenario, we use the shipment that it refers to.
         * When the assignment is not controlled, we can use the shipment info from the other scenario if the shipment id is controlled.
         * The shipment may be controlled separately in the case an order is manually removed from a shipment, in which case the assignment doesn't usually have any change. */
        const primaryPatch =
          isAssignmentControlled || isEntryShipmentControlled
            ? entryScenarioPatch
            : isOtherShipmentControlled
              ? otherScenarioPatch
              : entryScenarioPatch;
        const otherPatch =
          primaryPatch === entryScenarioPatch
            ? otherScenarioPatch
            : entryScenarioPatch;

        const patch: ScenarioOrderPatch | null = primaryPatch && {
          ...primaryPatch,
          patch: {
            // Apply the other patch as base. We might have e.g. voyage charter updates in current scenario, but want to overwrite shipment id from master as the primaryPatch
            ...otherPatch?.patch,
            ...primaryPatch.patch,
          },
        };
        return [orderId, patch];
      }),
    ),
  );

  const mergeEntryStates = (
    targetStates: UserSchedule['entryStates'],
    sourceStates: UserSchedule['entryStates'],
  ): UserSchedule['entryStates'] => {
    return new Map(
      [...targetStates, ...sourceStates]
        .map(([clientId, state]): [ClientId, EntryState] | null => {
          const keptEntry = allKeptEntries.get(clientId);
          if (!keptEntry && state === EntryState.DELETED) {
            return [clientId, EntryState.DELETED];
          } else if (keptEntry && state === EntryState.UPDATED) {
            return [clientId, state];
          } else {
            return null;
          }
        })
        .filter(isDefined),
    );
  };

  const getMergedPortCalls = (): PortCallEntry[] => {
    /** All port calls which are referred to by any entry in the resulting scenario */
    const targetPortCallsById = mapKeyBy(target.portCalls, (p) => p.clientId);
    const sourcePortCallsById = mapKeyBy(source.portCalls, (p) => p.clientId);

    const seen = new Set<ClientId>();
    const portCalls: PortCallEntry[] = [];

    const add = (portCallId: ClientId | null) => {
      if (!portCallId || seen.has(portCallId)) {
        return;
      }
      seen.add(portCallId);
      const targetPc = targetPortCallsById.get(portCallId) ?? null;
      const sourcePc = sourcePortCallsById.get(portCallId) ?? null;
      const resolved = resolveKeptEntry(targetPc, sourcePc, false);
      if (resolved) {
        portCalls.push(resolved);
      }
    };

    for (const assignments of [
      unadjustedSchedule,
      coaAssignments,
      spotAssignments,
    ]) {
      for (const a of assignments) {
        if ('loadingPortCallId' in a) {
          add(a.loadingPortCallId);
          add(a.dischargingPortCallId);
        } else if ('portCallId' in a) {
          add(a.portCallId);
        }
      }
    }

    return portCalls;
  };

  // Use the row's own `updatedAt`, not `getScenarioUpdatedAt(s)` — that
  // returns the max across nested entries, so a master commit would snap
  // every auto-syncing user scenario to "Today" in the scenarios panel.
  const mergedScenarioUpdatedAt = opts.sourceIsCommit
    ? (originalTarget.updatedAt ?? null)
    : (() => {
        const t = dateStringToNumOpt(originalTarget.updatedAt) ?? 0;
        const s = dateStringToNumOpt(originalSource.updatedAt) ?? 0;
        const maxRow = Math.max(t, s);
        return maxRow > 0 ? numToDateString(maxRow) : null;
      })();

  const patchShipments = new Map(
    filteredMap(
      [
        ...('patchShipments' in target ? target.patchShipments.keys() : []),
        ...('patchShipments' in source ? source.patchShipments.keys() : []),
      ],
      (shipmentId) => {
        const targetPatch = target.patchShipments.get(shipmentId);
        const sourcePatch = source.patchShipments.get(shipmentId);
        // We always use the master version if it exists, because changes to shipments attributes are saved to db immediately when the shipment is already committed before
        if (sourcePatch) {
          return [shipmentId, sourcePatch];
        } else if (targetPatch) {
          return [shipmentId, targetPatch];
        } else {
          return NONE;
        }
      },
    ),
  );

  const orderShortlist = ((target.orderShortlist?.updatedAt ?? 0) >
  (source.orderShortlist?.updatedAt ?? 0)
    ? target.orderShortlist
    : source.orderShortlist) ?? { orderIds: [], updatedAt: 0 };

  const vesselsShortlist = ((target.vesselsShortlist?.updatedAt ?? 0) >
  (source.vesselsShortlist?.updatedAt ?? 0)
    ? target.vesselsShortlist
    : source.vesselsShortlist) ?? {
    vesselIds: [],
    updatedAt: 0,
  };

  const freightByShipmentClientId =
    (target.changedAt ?? 0) > (source.changedAt ?? 0)
      ? target.freightByShipmentClientId
      : source.freightByShipmentClientId;

  const extraFreightByShipmentClientId =
    (target.changedAt ?? 0) > (source.changedAt ?? 0)
      ? target.extraFreightByShipmentClientId
      : source.extraFreightByShipmentClientId;

  /** Merged port calls, but times have not been fixed */
  const unadjustedPortCalls = getMergedPortCalls();

  const schedule = mapKeyBy(unadjustedSchedule, (e) => e.clientId);
  const portCalls = mapKeyBy(unadjustedPortCalls, (pc) => pc.clientId);

  applyMergeDirectives({ tcEntries: schedule, patchOrders }, opts.directives);

  const estimateTimelines = originalTarget.estimateTimelines;
  const coreProps: Required<Omit<CoreScenario, 'estimateTimelines'>> &
    Pick<CoreScenario, 'estimateTimelines'> = {
    dataSchemaVersion: originalTarget.dataSchemaVersion,
    clientId: originalTarget.clientId,
    createdBy: originalTarget.createdBy,
    createdAt: originalTarget.createdAt ?? null,
    description: originalTarget.description ?? '',
    shared: originalTarget.shared,
    organizationId: originalTarget.organizationId,
    initials: originalTarget.initials ?? '',
    changedAt: originalTarget.changedAt ?? null,
    updatedBy: originalTarget.updatedBy,
    integrationData: originalTarget.integrationData ?? null,
    importedAt: originalTarget.importedAt ?? null,
    updatedAt: mergedScenarioUpdatedAt,
    schedule: [...schedule.values()],
    coaAssignments,
    spotAssignments,
    cargoAllocations,
    portCalls: [...portCalls.values()],
    masterCommitAt: opts.sourceIsCommit
      ? (originalSource.masterCommitAt ?? null)
      : (originalTarget.masterCommitAt ?? null),
    patchOrders,
    voyageCharterByOrderId,
    isReadOnly: opts.sourceIsCommit
      ? target.isReadOnly // Syncing with master should not affect read-only status
      : (source.isReadOnly ?? target.isReadOnly ?? target.type !== 'user'),
    isIntegrationMaster:
      (opts.sourceIsCommit
        ? target.isIntegrationMaster // Syncing with master should not affect this flag
        : (source.isIntegrationMaster ?? target.isIntegrationMaster)) ?? false, // But updating from newer version of the same scenario should
    autoUpdateFromMaster:
      (opts.sourceIsCommit
        ? target.autoUpdateFromMaster // Syncing with master should not affect this flag
        : (source.autoUpdateFromMaster ?? target.autoUpdateFromMaster)) ?? // But updating from newer version of the same scenario should
      'default',
    name: (!opts.sourceIsCommit && source.name) || target.name || null,
    deletedAt: source.deletedAt || target.deletedAt || null,
    patchShipments,
    orderShortlist,
    vesselsShortlist,
    estimateTimelines: estimateTimelines?.length
      ? estimateTimelines
      : undefined,
    freightByShipmentClientId,
    extraFreightByShipmentClientId,
  };

  if (isUserScenario(originalTarget)) {
    const res: Required<Omit<UserSchedule, 'estimateTimelines'>> &
      Pick<UserSchedule, 'estimateTimelines'> = {
      ...coreProps,
      type: originalTarget.type,
      id: resolveMergedScenarioId(originalTarget, originalSource),
      migrationVersion: originalTarget.migrationVersion,
      syncedWithMasterAt: opts.sourceIsCommit
        ? (originalSource.masterCommitAt ?? Date.now())
        : (originalTarget.syncedWithMasterAt ?? null),
      entryStates: mergeEntryStates(targetEntryStates, sourceEntryStates),
    };
    return res as typeof originalTarget;
  } else if (isSuggestionScenario(originalTarget)) {
    const res: Required<
      Omit<SuggestionSchedule, 'estimateTimelines' | 'cleanSlateLayer'>
    > &
      Pick<SuggestionSchedule, 'estimateTimelines' | 'cleanSlateLayer'> = {
      ...coreProps,
      type: originalTarget.type,
      id: resolveMergedScenarioId(originalTarget, originalSource),
      baseScenarioId: originalTarget.baseScenarioId,
      cleanSlateLayer: originalTarget.cleanSlateLayer,
      migrationVersion: originalTarget.migrationVersion,
      syncedWithMasterAt: opts.sourceIsCommit
        ? (originalSource.masterCommitAt ?? Date.now())
        : (originalTarget.syncedWithMasterAt ?? Date.now()),
      entryStates: mergeEntryStates(targetEntryStates, sourceEntryStates),
    };
    return res as typeof originalTarget;
  } else if (isMasterScenario(originalTarget)) {
    const res: Required<Omit<MasterSchedule, 'estimateTimelines'>> &
      Pick<MasterSchedule, 'estimateTimelines'> = {
      ...coreProps,
      type: originalTarget.type,
      isReadOnly: originalTarget.isReadOnly,
    };
    return res as typeof originalTarget;
  } else if (isBackupScenario(originalTarget)) {
    const res: Required<Omit<BackupSchedule, 'estimateTimelines'>> &
      Pick<BackupSchedule, 'estimateTimelines'> = {
      ...coreProps,
      type: originalTarget.type,
      backedUpAt: originalTarget.backedUpAt,
    };
    return res as typeof originalTarget;
  } else if (isInitialScenario(originalTarget)) {
    const res: Required<Omit<InitialSchedule, 'estimateTimelines'>> &
      Pick<InitialSchedule, 'estimateTimelines'> = {
      ...coreProps,
      type: originalTarget.type,
    };
    return res as typeof originalTarget;
  } else {
    throw new Error('Unknown scenario type');
  }
});

type EventTiming = {
  start: number;
  duration: number;
  type: CoreEventType;
  entry: Exclude<ScheduleEntry, CoaEntry | SpotEntry>;
};

/** Splits entries to individual, sortable events */
const entryToEvents = (entry: TCScheduleEntry): EventTiming[] => {
  return 'loadingStart' in entry
    ? [
        {
          start: entry.loadingStart,
          duration: entry.loadingDuration,
          type: CoreEventType.Load,
          entry,
        },
        {
          start: entry.dischargingStart,
          duration: entry.dischargingDuration,
          type: CoreEventType.Discharge,
          entry,
        },
      ]
    : isSingleEntry(entry)
      ? [
          {
            start: entry.start,
            duration: entry.duration,
            type: CoreEventType.Single,
            entry,
          },
        ]
      : [
          {
            start: entry.start,
            duration: entry.duration,
            type: CoreEventType.OffHire,
            entry,
          },
        ];
};

/** Mutates first param */
const applyEventToEntry = (
  entry: TCScheduleEntry,
  event: EventTiming,
): void => {
  const { start, duration } = event;
  if (isCargoEntry(entry)) {
    if (event.type === CoreEventType.Load) {
      entry.loadingStart = start;
      entry.loadingEnd = start + duration;
      entry.loadingDuration = duration;
    } else {
      entry.dischargingStart = start;
      entry.dischargingEnd = start + duration;
      entry.dischargingDuration = duration;
    }
  } else {
    entry.start = start;
    entry.end = start + duration;
    entry.duration = duration;
  }
};

/** Given a sequence of events, ensure that start times are increasing */
function fixStartTimesInSequence(
  events: EventTiming[],
  firstIndex: number = 0,
) {
  for (let i = firstIndex; i < events.length; i++) {
    const previous = events[i - 1];
    events[i].start = Math.max(
      previous.start + previous.duration,
      events[i].start,
    );
  }
}

function applyMergeDirectives(
  {
    tcEntries,
    patchOrders,
  }: {
    tcEntries: Map<ClientId, TCScheduleEntry>;
    patchOrders: UserSchedule['patchOrders'];
  },
  directives: MergeDirectives | undefined,
) {
  // Fix some start times when new entries are supposed to be sequential with another entry

  const entriesByVessel = mapGroupBy(
    [...tcEntries.values()].map((e) => ({ ...e })),
    (e) => e.vesselId,
  );

  const getEventShipmentId = (event: EventTiming) =>
    isCargoEntry(event.entry)
      ? (patchOrders.get(event.entry.orderId)?.patch.shipmentId ??
        event.entry.shipmentId)
      : null;

  for (const [vesselId, vesselEntries] of entriesByVessel) {
    if (!isDefined(vesselId)) continue;

    const vesselEvents = vesselEntries
      .flatMap(entryToEvents)
      .sort((a, b) => a.start - b.start);

    /** For every shipment directive, splice the entries belonging to "next" shipment and adjust their timings so the shipment begins right after "previous" */
    for (const [next, previous] of directives?.placeShipmentAfter ?? []) {
      const [movingEvents, schedule] = partition(
        vesselEvents,
        (e) => getEventShipmentId(e) === next,
      );

      const lastPreviousIndex = findLastIndex(
        schedule,
        (e) => getEventShipmentId(e) === previous,
      );
      if (lastPreviousIndex === -1 || movingEvents.length === 0) {
        continue;
      }

      schedule.splice(lastPreviousIndex + 1, 0, ...movingEvents);
      fixStartTimesInSequence(schedule, lastPreviousIndex + 1);
      for (let i = lastPreviousIndex + 1; i < schedule.length; i++) {
        const event = schedule[i];
        applyEventToEntry(event.entry, event);
        tcEntries.set(event.entry.clientId, event.entry);
      }
    }
  }
}
